import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';

import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { ACCESS_DENIED_URL, readAccessDenial } from 'app/core/security/access-denied.service';
import {
    describeCurrentRole
    , describeModules
    , describeRequirement
    , describeRoles
} from 'app/core/security/access-requirement';
import { DEFAULT_RETURN_URL } from 'app/core/security/login-redirect';
import { SecurityService } from 'app/core/security/security.service';
import { RequestAccessService } from 'app/shared/permissions-banner/request-access.service';

/**
 * Shown in place of a page the user may not open, with the attempted URL left in the address bar (see
 * AccessDeniedService). Says what the page needs and what the user has, with a way back and a way to ask.
 */
@Component({
    selector: 'pcg-access-denied'
    , changeDetection: ChangeDetectionStrategy.OnPush
    , imports: [RouterLink]
    , templateUrl: './access-denied.component.html'
    , styleUrl: './access-denied.component.scss'
})
export class AccessDeniedComponent {
    private readonly router = inject(Router);
    private readonly location = inject(Location);
    private readonly sec = inject(SecurityService);
    private readonly requestAccessService = inject(RequestAccessService);

    /** The denial of the navigation that built this component, read while that navigation is still current. */
    private readonly firstDenial = readAccessDenial(this.router.currentNavigation()?.extras.state ?? this.location.getState());

    /**
     * What to explain: the denial the latest navigation here carried. The router reuses this component when one
     * Access Denied page follows another (Back/Forward between two of them), so a value read once would go stale.
     */
    readonly denial = computed(() => {
        const navigation = this.router.lastSuccessfulNavigation();
        const isHere = !!navigation
            && this.router.serializeUrl(navigation.finalUrl ?? navigation.extractedUrl) === ACCESS_DENIED_URL;
        return isHere ? readAccessDenial(navigation.extras.state) : this.firstDenial;
    });

    /** The attempted page, without its query string, which can carry search text. */
    readonly page = computed(() => this.denial()?.url.split(/[?#]/)[0] || null);

    /** Each way into the page, with the user's role in the modules it names. Follows the user when it reloads. */
    readonly requirements = computed(() => {
        const moduleAccess = this.sec.getUser()?.moduleAccess;
        return (this.denial()?.requirements ?? []).map(o => {
            const modules = describeModules(o.modules);
            return {
                modules
                , roles: describeRoles(o.permissionRoles)
                , current: modules === 'any module'
                    ? []
                    : [...new Set(o.modules)].map(m => ({
                        module: BusinessAreaEnum.toDescription(m)
                        , role: describeCurrentRole(moduleAccess, m)
                    }))
            };
        });
    });

    /** Tickets need the Support module, as CreateTicket does; without it the page points to an administrator. */
    readonly canRequestAccess = computed(() => !!this.sec.hasModuleAccess(
        [BusinessAreaEnum.HelpDesk]
        , SecurityService.setMinRole(PermissionRole.User)
    ));

    constructor() { inject(Title).setTitle('Access Denied'); }

    /** Back where they came from, unless that would leave HOMER (a deep link, a new tab): then the dashboard. */
    goBack() {
        if (this.canGoBack()) { this.location.back(); }
        else { this.router.navigateByUrl(DEFAULT_RETURN_URL); }
    }

    /** Opens an Account Permissions ticket that names the page and the access it needs. */
    requestAccess() {
        const denial = this.denial();
        this.requestAccessService.requestAccess(
            denial ? { page: this.page() ?? denial.url, access: this.accessNeeded() } : undefined
        );
    }

    private accessNeeded(): string {
        const denial = this.denial();
        if (denial?.reason === 'module') {
            const needed = denial.requirements?.length
                ? denial.requirements.map(describeRequirement).join(' or ')
                : 'Access to any module';
            return denial.message ? `${needed}. ${denial.message}` : needed;
        }
        if (denial?.reason === 'server') {
            return `Not stated. The server refused a request the page makes${denial.message ? `: ${denial.message}` : '.'}`;
        }
        return denial?.message ?? '';
    }

    /**
     * Whether Back stays in HOMER. The Navigation API knows, since it lists only this tab's same-origin entries;
     * without it, fall back to whether HOMER showed a page before this one.
     */
    private canGoBack(): boolean {
        const navigation = (globalThis as { navigation?: { canGoBack?: unknown } }).navigation;
        if (typeof navigation?.canGoBack === 'boolean') { return navigation.canGoBack; }
        return !!this.router.lastSuccessfulNavigation()?.previousNavigation;
    }
}
